import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Palette, TextT } from "@phosphor-icons/react";
import { defaultHobby, hobbySchema } from "@stylingcv/schema";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from "@stylingcv/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { SectionDialog } from "../sections/shared/section-dialog";

// Available hobby icons with their display names - comprehensive list
export const hobbyIcons = [
  // Sports & Athletic Activities
  { class: "Tennis", name: "Tennis" },
  { class: "Football", name: "Football" },
  { class: "Basket-Ball", name: "Basketball" },
  { class: "Swimming", name: "Swimming" },
  { class: "Volleyball", name: "Volleyball" },
  { class: "Badminton", name: "Badminton" },
  { class: "Table-Tennis", name: "Table Tennis" },
  { class: "Cricket", name: "Cricket" },
  { class: "Rugby", name: "Rugby" },
  { class: "Running", name: "Running" },
  { class: "Cycling", name: "Cycling" },
  { class: "Bowling", name: "Bowling" },
  { class: "Archery", name: "Archery" },
  { class: "Dart", name: "Dart" },
  { class: "Climbing", name: "Climbing" },
  { class: "Ski", name: "Skiing" },
  { class: "Snow-Boarding", name: "Snowboarding" },
  { class: "Ice-Hockey", name: "Ice Hockey" },
  { class: "Ice-Skating", name: "Ice Skating" },
  { class: "Horse-Riding", name: "Horse Riding" },
  { class: "Karate", name: "Karate" },
  { class: "Gymnastic", name: "Gymnastics" },
  { class: "Yoga", name: "Yoga" },
  { class: "Weight-Lifting", name: "Weight Lifting" },
  { class: "Gym", name: "Gym" },
  { class: "Treadmill", name: "Treadmill" },
  { class: "Jog", name: "Jogging" },

  // Arts & Creative
  { class: "Music", name: "Music" },
  { class: "Guitar", name: "Guitar" },
  { class: "Play-Piano", name: "Piano" },
  { class: "Play-Violin", name: "Violin" },
  { class: "Singing", name: "Singing" },
  { class: "Dancing", name: "Dancing" },
  { class: "Painting", name: "Painting" },
  { class: "Photography", name: "Photography" },
  { class: "Reading", name: "Reading" },
  { class: "Sculpting", name: "Sculpting" },

  // Entertainment & Media
  { class: "Movies", name: "Movies" },
  { class: "Cinema", name: "Cinema" },
  { class: "Horror-Movies", name: "Horror Movies" },
  { class: "TV", name: "TV" },
  { class: "Watching-TV", name: "Watching TV" },
  { class: "Games", name: "Games" },
  { class: "Xbox", name: "Xbox Gaming" },
  { class: "Radio", name: "Radio" },
  { class: "News", name: "News" },
  { class: "News-paper", name: "Newspaper" },

  // Food & Cooking
  { class: "Cooking", name: "Cooking" },
  { class: "Baking", name: "Baking" },
  { class: "Eating", name: "Eating" },
  { class: "Drinking", name: "Drinking" },
  { class: "Wine-Tasting", name: "Wine Tasting" },

  // Outdoor & Adventure
  { class: "Travelling", name: "Travelling" },
  { class: "Holidays", name: "Holidays" },
  { class: "Fishing", name: "Fishing" },
  { class: "Gardening", name: "Gardening" },
  { class: "Hill-Walking", name: "Hill Walking" },
  { class: "Walking", name: "Walking" },
  { class: "Boating", name: "Boating" },
  { class: "Kite-Flying", name: "Kite Flying" },
  { class: "Park", name: "Park Visits" },
  { class: "Historic-places", name: "Historic Places" },

  // Social & Lifestyle
  { class: "Family", name: "Family Time" },
  { class: "Friends", name: "Friends" },
  { class: "Meet-People", name: "Meeting People" },
  { class: "Socialising", name: "Socializing" },
  { class: "Dating", name: "Dating" },
  { class: "Talking", name: "Talking" },
  { class: "On-Phone", name: "Phone Calls" },
  { class: "Partying", name: "Partying" },
  { class: "Gossips", name: "Gossips" },

  // Games & Puzzles
  { class: "Cards", name: "Cards" },
  { class: "Chess", name: "Chess" },
  { class: "Poker", name: "Poker" },
  { class: "Pool", name: "Pool" },
  { class: "Gambling", name: "Gambling" },

  // Animals & Pets
  { class: "Play-with-Dog", name: "Playing with Dog" },
  { class: "Dog-Walking", name: "Dog Walking" },
  { class: "Baby", name: "Baby Care" },
  { class: "Feed-Baby", name: "Feeding Baby" },

  // Technology & Learning
  { class: "Web-Surfing", name: "Web Surfing" },
  { class: "Online-Shop", name: "Online Shopping" },

  // Hobbies & Crafts
  { class: "Shopping", name: "Shopping" },
  { class: "Cleaning", name: "Cleaning" },
  { class: "House-Repair", name: "House Repair" },
  { class: "Wash-car", name: "Car Washing" },
  { class: "Galleries", name: "Art Galleries" },
  { class: "Shooting", name: "Shooting" },

  // Transportation
  { class: "Driving", name: "Driving" },
  { class: "Motor-Bike", name: "Motorcycle" },

  // Relaxation & Rest
  { class: "Sleeping", name: "Sleeping" },
  { class: "sleeping-1", name: "Napping" },
  { class: "Relaxing", name: "Relaxing" },
  { class: "Dreaming", name: "Dreaming" },
  { class: "Taking-Bath", name: "Taking Bath" },

  // Unique & Fun
  { class: "Ghost-Hunting", name: "Ghost Hunting" },
  { class: "Grow-Moustache", name: "Growing Moustache" },
  { class: "Smoking", name: "Smoking" },
  { class: "Twimming", name: "Swimming (Alt)" },
];

const formSchema = hobbySchema;
type FormValues = z.infer<typeof formSchema>;

export const HobbiesDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: { ...defaultHobby, icon: "Tennis" },
    resolver: zodResolver(formSchema),
  });

  const selectedIcon = form.watch("icon") || "Tennis";

  // Update form when icon is selected
  const handleIconSelect = (iconClass: string) => {
    form.setValue("icon", iconClass, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <SectionDialog<FormValues> id="hobbies" form={form} defaultValues={defaultHobby}>
      <div className="grid grid-cols-1 gap-6 p-2">
        {/* Hobby Name Input */}
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Hobby Name`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t`e.g., Photography, Reading, Gaming`} leadingIcon={<Palette size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description Input */}
        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description (Optional)`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t`Brief description of your hobby`} leadingIcon={<TextT size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Icon Selection */}
        <FormField
          name="icon"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Select Icon`}</FormLabel>
              <FormControl>
                <div className="space-y-4">
                  {/* Selected Icon Preview */}
                  <div className="flex items-center gap-3 rounded-lg border bg-secondary/30 p-4">
                    <i
                      className={`stylingcvHobbies iconHobbies ${selectedIcon} text-2xl text-primary`}
                    ></i>
                    <span className="font-medium">{t`Selected Icon`}</span>
                  </div>

                  {/* Icon Grid */}
                  <div className="grid max-h-80 grid-cols-6 gap-2 overflow-y-auto bg-gray-50/50 p-3 dark:bg-gray-800/50">
                    {hobbyIcons.map((icon) => (
                      <button
                        key={icon.class}
                        type="button"
                        className={`flex flex-col items-center justify-center p-2 transition-all duration-200 hover:scale-105 hover:bg-secondary/50 hover:shadow-sm ${
                          selectedIcon === icon.class
                            ? "bg-primary/15 shadow-md"
                            : "bg-white dark:bg-gray-700"
                        } `}
                        title={icon.name}
                        onClick={() => {
                          handleIconSelect(icon.class);
                        }}
                      >
                        <i
                          className={`stylingcvHobbies iconHobbies ${icon.class} mb-1 text-primary`}
                          style={{ fontSize: "70px" }}
                        ></i>
                      </button>
                    ))}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
